import { Box, Paper, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
	ReactNode,
	type PointerEvent as ReactPointerEvent,
	useRef,
	useState,
} from "react";
import { attachHorizontalDrag } from "../../utils/horizontal-drag";
import { getDisplayName } from "../../utils/privacy";
import { readStoredPct, writeStoredPct } from "../../utils/resize-persistence";
import { ResizeHandle } from "../common/ResizeHandle";
import { useGlobalState } from "../globalState";
import { PersonDetails } from "../grading/person-details";
import type { ActivityFilter } from "./communication-timeline/timeline-types";
import { PersonActionsTabs } from "./person-actions-tabs";
import { PersonEducation } from "./person-education";
import { PersonExperience } from "./person-experience";
import { PersonLanguage } from "./person-language";
import { PersonProfileSections } from "./person-profile-sections";
import { PersonSkills } from "./person-skills";
import { PersonSummary } from "./person-summary";

interface PersonContentProps {
	personId: number;
	personDetails: PersonDetails;
	/** Optional assignment ID for keyword highlighting */
	assignmentId?: number;
	/** Optional header content rendered above the left column */
	headerSlot?: ReactNode;
	/** Initial left-column width as % of the container (draggable divider). */
	defaultLeftPct?: number;
	/** Scrollable height for the columns; callers get it from `useAvailableHeight`. */
	scrollHeight?: string;
	/** Active tab slug for PersonActionsTabs */
	activeTab?: string;
	/** Callback when tab changes */
	onTabChange?: (tabSlug: string) => void;
	/** Build href for a tab slug (enables middle-click) */
	buildTabHref?: (tabSlug: string) => string;
	/** Incremented externally to trigger PersonAssignments refetch */
	roleAddedCounter?: number;
	/** Forwarded to PersonAssignments: called after a mutation on the Roles tab. */
	onPersonAssignmentsChanged?: () => void;
	/** Pre-seed Activity tab filters (forwarded to PersonActionsTabs). */
	initialActivityFilters?: ActivityFilter[];
}

const LEFT_PCT_STORAGE_KEY = "person-content:left-pct";

/**
 * Shared person content component used by both PersonView and PersonModal.
 * Renders the person's profile information (summary, experience, education, etc.)
 *
 * Always two columns — left = profile, right = PersonActionsTabs.
 */
export const PersonContent = ({
	personId,
	personDetails,
	assignmentId,
	headerSlot,
	defaultLeftPct = 60,
	scrollHeight,
	activeTab,
	onTabChange,
	buildTabHref,
	roleAddedCounter,
	onPersonAssignmentsChanged,
	initialActivityFilters,
}: PersonContentProps) => {
	const { currentUser } = useGlobalState();
	const theme = useTheme();

	// Draggable split between the profile (left) and the actions tabs (right),
	// persisted so the user's chosen ratio survives reopening.
	const containerRef = useRef<HTMLDivElement>(null);
	const [leftPct, setLeftPct] = useState(() =>
		readStoredPct(LEFT_PCT_STORAGE_KEY, defaultLeftPct)
	);
	const startColumnResize = (e: ReactPointerEvent) => {
		e.preventDefault();
		const container = containerRef.current;
		if (!container) return;
		attachHorizontalDrag((clientX) => {
			const rect = container.getBoundingClientRect();
			const pct = ((clientX - rect.left) / rect.width) * 100;
			const clamped = Math.min(80, Math.max(20, pct));
			setLeftPct(clamped);
			writeStoredPct(LEFT_PCT_STORAGE_KEY, clamped);
		}, "col-resize");
	};

	const scrollSx = scrollHeight
		? {
				height: scrollHeight,
				overflowY: "auto" as const,
				scrollbarColor: `${theme.palette.action.active} transparent`,
				"&::-webkit-scrollbar": {
					width: 8,
					background: "transparent",
				},
				"&::-webkit-scrollbar-track": {
					background: "transparent",
				},
				"&::-webkit-scrollbar-thumb": {
					background: theme.palette.action.active,
					borderRadius: 4,
				},
			}
		: {};

	// If personSummary exists, show LLM summary instead of LinkedIn-derived profile
	const isSummaryMode = !!personDetails.personSummary;

	// Profile sections (no header) — rendered in the left column.
	const profileSections = (
		<>
			<Grid size={12} data-component="summary-grid">
				{isSummaryMode ? (
					<Paper data-component="llm-summary-only" sx={{ p: 2 }}>
						<Typography variant="h6" gutterBottom>
							{getDisplayName(
								personDetails.personSummary.name,
								personId,
								currentUser?.userprofile
							)}
						</Typography>
						<Typography variant="body1">
							{personDetails.personSummary.llm_summary}
						</Typography>
					</Paper>
				) : (
					<PersonSummary
						personInfo={personDetails.personInfo}
						showFormattedSummary={true}
					/>
				)}
			</Grid>

			{!isSummaryMode ? (
				<Grid size={12} data-component="experience-grid">
					<PersonExperience
						experience={personDetails.experience}
						personId={personId}
						assignmentId={assignmentId}
					/>
				</Grid>
			) : null}

			{!isSummaryMode ? (
				<>
					<Grid size={12} data-component="education-grid">
						<PersonEducation education={personDetails.education} />
					</Grid>
					<Grid size={12} data-component="language-grid">
						<PersonLanguage languages={personDetails.languages} />
					</Grid>
					<Grid size={12} data-component="skills-grid">
						<PersonSkills
							personSkills={personDetails.personSkills}
							personId={personId}
							assignmentId={assignmentId}
							prefetchedAdditionalSkills={personDetails.additionalSkills}
						/>
					</Grid>
					{personDetails.profileSections?.length > 0 ? (
						<PersonProfileSections
							sections={personDetails.profileSections}
						/>
					) : null}
				</>
			) : null}
		</>
	);

	const actionsTabs = (
		<PersonActionsTabs
			personId={personId}
			personInfo={personDetails.personInfo}
			assignmentId={assignmentId}
			isSuperuser={currentUser?.is_superuser || false}
			jobPostings={personDetails.jobPostings}
			personEmails={personDetails.personEmails}
			personPhoneNumbers={personDetails.personPhonenumber}
			activeTab={activeTab}
			onTabChange={onTabChange}
			buildTabHref={buildTabHref}
			roleAddedCounter={roleAddedCounter}
			onPersonAssignmentsChanged={onPersonAssignmentsChanged}
			initialActivityFilters={initialActivityFilters}
		/>
	);

	return (
		<Box data-component="person-content-container" sx={{ height: "100%" }}>
			<Box ref={containerRef} sx={{ display: "flex", height: "100%" }}>
				{/* Left Column - Profile Content */}
				<Box
					data-component="person-left-column"
					sx={{ width: `${leftPct}%`, minWidth: 0, ...scrollSx }}
				>
					<Grid
						container
						justifyContent="center"
						spacing={2}
						data-component="person-left-content"
					>
						{headerSlot ? (
							<Grid size={12} data-component="header-slot">
								{headerSlot}
							</Grid>
						) : null}
						{profileSections}
					</Grid>
				</Box>

				{/* Draggable divider between the two columns. */}
				<ResizeHandle
					onPointerDown={startColumnResize}
					dataComponent="drawer-resize-handle"
				/>

				{/* Right Column - PersonActionsTabs */}
				<Box
					data-component="person-right-column"
					sx={{ flex: 1, minWidth: 0, ...scrollSx }}
				>
					{actionsTabs}
				</Box>
			</Box>
		</Box>
	);
};
